import { isSearchResultsAttractionsEnabled } from "../assets/GlobalVariables";
import { searchAttractions } from "../services/attractionsApi";
import {
  pickInternalDestinationFromResolveResponse,
  resolveDestination,
} from "../services/destinationsApi";
import { getDestinationResolveHints } from "./resolveAttractionsDestinationId";

function parseDdMmYyyyToIso(s) {
  if (!s || typeof s !== "string") return null;
  const parts = s.split("/");
  if (parts.length !== 3) return null;
  const [dd, mm, yyyy] = parts;
  if (!dd || !mm || !yyyy) return null;
  return `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
}

function debugAttractionsEnabled() {
  return (
    import.meta.env.DEV &&
    typeof localStorage !== "undefined" &&
    localStorage.getItem("DEBUG_ATTRACTIONS_SEARCH") === "true"
  );
}

/** זמני: localStorage DEBUG_ATTRACTIONS_FORCE_FALLBACK_HINTS === "true" — דילוג על חוסר רמזים עם ATH/Athens */
function shouldForceFallbackHints() {
  return (
    typeof localStorage !== "undefined" &&
    localStorage.getItem("DEBUG_ATTRACTIONS_FORCE_FALLBACK_HINTS") === "true"
  );
}

/**
 * חיפוש אטרקציות מקביל לחיפוש LDS/מלונות — לפי קוד יעד (למשל ATH) דרך /api/destinations/resolve ואז GET /api/attractions/search.
 *
 * @param {{
 *   location: object | null,
 *   calendarDateArrival?: string,
 *   calendarDateLeave?: string,
 *   roomInfo?: { rooms?: unknown[] },
 *   searchPhrase?: object | null,
 * }} input
 */
export async function runParallelAttractionsSearch(input = {}) {
  console.log("[HOTELS ATTRACTIONS DEBUG] RUN START", input);
  const {
    location,
    calendarDateArrival = "",
    calendarDateLeave = "",
    roomInfo = null,
    searchPhrase = null,
  } = input;

  if (!isSearchResultsAttractionsEnabled()) {
    console.log("[HOTELS ATTRACTIONS DEBUG] STOP", { reason: "feature_off" });
    return { skipped: true, attractions: [], meta: { reason: "feature_off" } };
  }

  const hints = getDestinationResolveHints(location, searchPhrase);

  console.log("[HOTELS ATTRACTIONS DEBUG] DESTINATION HINTS", {
    location,
    searchPhrase,
    hints,
  });

  let lds = String(hints.lds_code || "").trim().toUpperCase();
  let name = String(hints.name || "").trim();

  if (lds.length < 2 && name.length < 2) {
    if (shouldForceFallbackHints()) {
      const fallbackHints = { lds_code: "ATH", name: "Athens" };
      console.log("ATTRACTIONS DEBUG: using fallback hints", fallbackHints);
      lds = fallbackHints.lds_code;
      name = fallbackHints.name;
    } else {
      console.log("[HOTELS ATTRACTIONS DEBUG] STOP", {
        reason: "no_destination_hint",
      });
      return {
        skipped: true,
        attractions: [],
        meta: { reason: "no_destination_hint", lds_code: lds, name },
      };
    }
  } else if (lds.length >= 2) {
    lds = lds.toUpperCase();
  }

  const dateFrom =
    parseDdMmYyyyToIso(String(calendarDateArrival || "").trim()) || null;
  const dateTo =
    parseDdMmYyyyToIso(String(calendarDateLeave || "").trim()) || dateFrom;

  if (debugAttractionsEnabled()) {
    console.log("[parallelAttractionsSearch] start", {
      lds,
      name,
      dateFrom,
      dateTo,
    });
  }

  let resolveResult;
  /** כשיש lds תקף — רק lds_code (ללא name) כדי שלא יישלחו מחרוזות בעברית יחד עם הקוד */
  const resolveParams =
    lds.length >= 2
      ? { lds_code: lds }
      : name
        ? { name }
        : {};
  console.log("CALLING RESOLVE", resolveParams);
  try {
    resolveResult =
      Object.keys(resolveParams).length > 0
        ? await resolveDestination(resolveParams)
        : { status: "not_found", destination_id: null, internal_destination_id: null };
  } catch (e) {
    console.log("RESOLVE RESULT", { error: true, message: e?.message, raw: e });
    if (debugAttractionsEnabled()) {
      console.warn("[parallelAttractionsSearch] resolveDestination failed", e);
    }
    console.log("[HOTELS ATTRACTIONS DEBUG] STOP", {
      reason: "resolve_destination_network_error",
    });
    return {
      skipped: false,
      attractions: [],
      meta: { lds_code: lds, name, resolveStatus: "network_error" },
      error: e?.message || "resolve_failed",
    };
  }

  console.log("RESOLVE RESULT", resolveResult);

  const pickedDestId = pickInternalDestinationFromResolveResponse(resolveResult);
  const ldsForSearch = String(
    resolveResult?.lds_code || lds || ""
  )
    .trim()
    .toUpperCase();
  const resolveSuccess =
    resolveResult &&
    (resolveResult.status === "ok" || resolveResult.status === "partial") &&
    (pickedDestId != null || ldsForSearch.length >= 2);
  if (!resolveSuccess) {
    if (debugAttractionsEnabled()) {
      console.log("[parallelAttractionsSearch] resolve not ok", resolveResult);
    }
    console.log("[HOTELS ATTRACTIONS DEBUG] STOP", {
      reason: "resolve_not_success",
    });
    return {
      skipped: false,
      attractions: [],
      meta: {
        lds_code: lds,
        name,
        resolveStatus: resolveResult?.status || "not_found",
        destinationId: null,
      },
    };
  }

  const destinationId = pickedDestId;

  console.log("CALLING ATTRACTIONS SEARCH", {
    internal_destination_id: destinationId ?? undefined,
    lds_code: destinationId == null ? ldsForSearch || undefined : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || dateFrom || undefined,
  });

  try {
    const data = await searchAttractions({
      ...(destinationId != null
        ? { destination_id: destinationId }
        : ldsForSearch.length >= 2
          ? { lds_code: ldsForSearch }
          : {}),
      date_from: dateFrom || undefined,
      date_to: dateTo || dateFrom || undefined,
      limit: 12,
      for_packages: true,
      for_hotel_upsell: true,
    });
    const list = Array.isArray(data?.attractions) ? data.attractions : [];
    if (debugAttractionsEnabled()) {
      console.log("[parallelAttractionsSearch] searchAttractions ok", {
        destinationId: destinationId ?? null,
        ldsFallback: destinationId == null ? ldsForSearch : null,
        count: list.length,
      });
    }
    const ldsMeta = String(lds || resolveResult.lds_code || "")
      .trim()
      .toUpperCase();
    return {
      skipped: false,
      attractions: list,
      meta: {
        /** מזהה לחיפוש אטרקציות — internal קודם (אם קיים ב-resolve) */
        destinationId: destinationId ?? null,
        /** מפורש ל-GET /api/attractions/rules — אותו ערך כמו ב-resolve כשקיים */
        internal_destination_id:
          resolveResult?.internal_destination_id != null
            ? Number(resolveResult.internal_destination_id)
            : destinationId ?? null,
        lds_code: ldsMeta,
        name: resolveResult.name || name,
      },
    };
  } catch (e) {
    if (debugAttractionsEnabled()) {
      console.warn("[parallelAttractionsSearch] searchAttractions failed", e);
    }
    console.log("[HOTELS ATTRACTIONS DEBUG] STOP", {
      reason: "attractions_search_failed",
    });
    return {
      skipped: false,
      attractions: [],
      meta: {
        destinationId: destinationId ?? null,
        lds_code: lds,
        name,
      },
      error: e?.message || "attractions_search_failed",
    };
  }
}
